/**
 * ReplacementSimulatorModal — Compare current vehicle tax vs replacement
 *
 * Side-by-side: current vehicle (read-only) vs new vehicle (CO2 + fuel type input).
 * Shows 3-year projection and cumulative savings.
 * "VE" shortcut button sets CO2=0 + electric.
 */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Receipt, X, Zap, TrendingDown } from 'lucide-react';
import { tvsAPI } from '../../api/index.js';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';

// Codes only — labels resolved at render via t() below.
const FUEL_TYPE_CODES = ['diesel', 'gasoline', 'hybrid', 'electric', 'hydrogen'];

function ReplacementSimulatorModal({ isOpen, onClose, vehicle }) {
  const { t } = useTranslation();
  // Devise de l'organisation du vehicule - un seul outil monetaire.
  const { money } = useOrgCurrency();
  const fuelTypeLabels = useMemo(
    () => Object.fromEntries(FUEL_TYPE_CODES.map((c) => [c, t(`fiscal.replacementSimulator.fuelTypes.${c}`)])),
    [t]
  );
  const formatEur = (n) => (n != null ? money(n, vehicle, { decimals: 0 }) : '—');
  const [newCo2, setNewCo2] = useState('');
  const [newFuel, setNewFuel] = useState('diesel');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSimulate = async () => {
    if (!newCo2 && newFuel !== 'electric' && newFuel !== 'hydrogen') return;
    setLoading(true);
    try {
      const res = await tvsAPI.simulateReplacement({
        vehicle_id: vehicle.id,
        new_co2_gkm: newFuel === 'electric' || newFuel === 'hydrogen' ? 0 : parseInt(newCo2),
        new_fuel_type: newFuel,
      });
      setResult(res.data);
    } catch {
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEVShortcut = () => {
    setNewCo2('0');
    setNewFuel('electric');
    // Auto-simulate
    setTimeout(() => {
      setLoading(true);
      tvsAPI.simulateReplacement({
        vehicle_id: vehicle.id,
        new_co2_gkm: 0,
        new_fuel_type: 'electric',
      }).then(res => setResult(res.data)).catch(() => setResult(null)).finally(() => setLoading(false));
    }, 0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-4 rounded-t-xl flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5" />
            <div>
              <h3 className="font-semibold">{t('fiscal.replacementSimulator.title')}</h3>
              <p className="text-xs text-emerald-200">{vehicle?.make} {vehicle?.model} — {vehicle?.license_plate}</p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-emerald-500 p-1 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4">
          {/* Current vehicle info */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-500 uppercase font-semibold mb-1">{t('fiscal.replacementSimulator.currentVehicle')}</p>
            <div className="flex items-center gap-4 text-sm">
              <span className="font-medium">{vehicle?.make} {vehicle?.model}</span>
              <span className="text-gray-500">{vehicle?.co2_gkm ? `${vehicle.co2_gkm} g/km` : t('fiscal.replacementSimulator.co2Unknown')}</span>
              <span className="text-gray-500">{vehicle?.fuel_type}</span>
            </div>
          </div>

          {/* New vehicle form */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('fiscal.replacementSimulator.co2Label')}</label>
              <input
                type="number"
                value={newCo2}
                onChange={e => setNewCo2(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                placeholder={t('fiscal.replacementSimulator.co2Placeholder')}
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('fiscal.replacementSimulator.energyLabel')}</label>
              <select
                value={newFuel}
                onChange={e => setNewFuel(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                {FUEL_TYPE_CODES.map((code) => (
                  <option key={code} value={code}>{fuelTypeLabels[code]}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={handleSimulate}
                disabled={loading || (!newCo2 && newFuel !== 'electric' && newFuel !== 'hydrogen')}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium disabled:opacity-50"
              >
                {loading ? t('fiscal.replacementSimulator.simulating') : t('fiscal.replacementSimulator.simulate')}
              </button>
              <button
                onClick={handleEVShortcut}
                className="px-3 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 text-sm font-medium flex items-center gap-1"
                title={t('fiscal.replacementSimulator.evShortcutTooltip')}
              >
                <Zap className="w-4 h-4" /> {t('fiscal.replacementSimulator.evShortcut')}
              </button>
            </div>
          </div>

          {/* Results */}
          {result && (
            <div className="space-y-3 pt-3 border-t border-gray-200">
              {/* Savings headline */}
              <div className="text-center p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-600 uppercase font-semibold">{t('fiscal.replacementSimulator.annualSaving2026')}</p>
                <p className="text-3xl font-bold text-green-700 flex items-center justify-center gap-2">
                  <TrendingDown className="w-6 h-6" />
                  {formatEur(result.annual_saving_2026)}
                </p>
                <p className="text-sm text-green-600 mt-1">
                  {t('fiscal.replacementSimulator.cumulativeSaving3yr')} <span className="font-bold">{formatEur(result.cumulative_saving_3yr)}</span>
                </p>
              </div>

              {/* Year-by-year comparison */}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 uppercase">
                    <th className="text-left py-1">{t('fiscal.replacementSimulator.table.year')}</th>
                    <th className="text-right py-1">{t('fiscal.replacementSimulator.table.currentVehicle')}</th>
                    <th className="text-right py-1">{t('fiscal.replacementSimulator.table.replacement')}</th>
                    <th className="text-right py-1">{t('fiscal.replacementSimulator.table.saving')}</th>
                  </tr>
                </thead>
                <tbody>
                  {[2025, 2026, 2027].map(y => (
                    <tr key={y} className={y === 2026 ? 'bg-blue-50 font-medium' : ''}>
                      <td className="py-1.5">{y}</td>
                      <td className="text-right text-red-700">{formatEur(result.current[`y${y}`])}</td>
                      <td className="text-right text-green-700">{formatEur(result.replacement[`y${y}`])}</td>
                      <td className="text-right font-semibold text-green-700">{formatEur(result.savings[`y${y}`])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReplacementSimulatorModal;
